class BulkOrderList extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.inputs = this.form.querySelectorAll('input[type="number"][data-variant-id]');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    this.submitButton = this.querySelector('[type="submit"]');

    if (this.form) {
      this.form.addEventListener('submit', this.submitHandler.bind(this));
    }
  }

  formatMoneyWithoutTrailingZeros(cents) {
    const formatted = Shopify.formatMoney(cents, Shopify.money_format);
    return cents === 0 ? formatted.replace('.00', '') : formatted;
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      event.stopPropagation();
      this.inputChangeHandler(event);
      this.updateTotalPrice();
    });

    this.updateTotalPrice();
  }

  submitHandler(e) {
    e.preventDefault();

    this.submitButton.setAttribute('aria-disabled', true);
    this.submitButton.classList.add('loading');
    this.querySelector('.loading__spinner').classList.remove('hidden');

    // Build items array, skipping any inputs with qty <= 0 (covers OOS variants)
    const availableItems = [];
    this.inputs.forEach((input) => {
      const qty = parseInt(input.value, 10);
      if (qty > 0) {
        availableItems.push({ id: input.dataset.variantId, quantity: qty });
      }
    });

    // Nothing valid to add — reset and bail
    if (availableItems.length === 0) {
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-disabled');
      this.querySelector('.loading__spinner').classList.add('hidden');
      return;
    }

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    // Empty FormData — avoids hidden form inputs sending OOS variant IDs
    const formData = new FormData();
    if (this.cart) {
      formData.append(
        'sections',
        this.cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      this.cart.setActiveElement(document.activeElement);
    }

    availableItems.forEach((item, index) => {
      formData.append(`items[${index}][id]`, item.id);
      formData.append(`items[${index}][quantity]`, item.quantity);
    });

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          for (const submitButtonText of this.submitButtonTexts) {
            submitButtonText.classList.add('hidden');
          }
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;

        CartPerformance.measure('add:paint-updated-sections', () => {
          this.cart.renderContents(response);
        });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        if (!this.error) this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner').classList.add('hidden');
      });
  }

  inputChangeHandler(event) {
    let input = event.target;
    let unitPrice = input.dataset.unitPrice;
    let unitPriceExVat = input.dataset.unitPriceExVat;

    let subtotalTarget = document.getElementById(input.dataset.subtotalTarget);
    let subtotalTargetExVat = document.getElementById(input.dataset.subtotalTargetExVat);

    if (subtotalTarget && subtotalTargetExVat) {
      const subtotal = Math.round(unitPrice * input.value);
      const subtotalExVat = Math.round(unitPriceExVat * input.value);
      subtotalTarget.innerHTML = this.formatMoneyWithoutTrailingZeros(subtotal);
      subtotalTargetExVat.innerHTML = this.formatMoneyWithoutTrailingZeros(subtotalExVat);
    }
  }

  updateTotalPrice() {
    let discountFactor = parseFloat(this.dataset.discountFactor);
    let priceTargetIncVat = this.querySelector('.bulk-order--total-price--including-vat');
    let discountedPriceTargetIncVat = this.querySelector('.bulk-order--total-disconted-price--including-vat');
    let priceTargetExcVat = this.querySelector('.bulk-order--total-price--excluding-vat');
    let discountedPriceTargetExcVat = this.querySelector('.bulk-order--total-disconted-price--excluding-vat');
    let totalContainer = this.querySelector('.bulk-order--total');
    let savingsBar = document.querySelector('savings-bar');

    let originalPriceIncVat = 0;
    let discountedPriceIncVat = 0;
    let originalPriceExcVat = 0;
    let discountedPriceExcVat = 0;

    if (priceTargetIncVat && savingsBar) {
      for (const input of this.inputs) {
        let unitPriceIncVat = parseFloat(input.dataset.unitPrice);
        let unitPriceExVat = parseFloat(input.dataset.unitPriceExVat);
        let originalUnitPriceIncVat = parseFloat(input.dataset.originalUnitPrice || unitPriceIncVat);
        let originalUnitPriceExVat = parseFloat(input.dataset.originalUnitPriceExVat || unitPriceExVat);

        originalPriceIncVat += originalUnitPriceIncVat * input.value;
        originalPriceExcVat += originalUnitPriceExVat * input.value;
        discountedPriceIncVat += unitPriceIncVat * input.value;
        discountedPriceExcVat += unitPriceExVat * input.value;
      }
    }

    priceTargetIncVat.innerHTML = this.formatMoneyWithoutTrailingZeros(Math.round(originalPriceIncVat));
    priceTargetExcVat.innerHTML = this.formatMoneyWithoutTrailingZeros(Math.round(originalPriceExcVat));
    const totalIsZero = originalPriceIncVat === 0;

    if (discountFactor < 1 && !totalIsZero) {
      discountedPriceTargetIncVat.innerHTML = this.formatMoneyWithoutTrailingZeros(Math.round(discountedPriceIncVat));
      discountedPriceTargetExcVat.innerHTML = this.formatMoneyWithoutTrailingZeros(Math.round(discountedPriceExcVat));
      if (discountedPriceTargetIncVat) discountedPriceTargetIncVat.classList.remove('hidden');
      if (discountedPriceTargetExcVat) discountedPriceTargetExcVat.classList.remove('hidden');
      if (totalContainer) {
        totalContainer.classList.add('bulk-order--total-discounted');
        totalContainer.classList.remove('bulk-order--total-standard');
      }
    } else {
      if (discountedPriceTargetIncVat) discountedPriceTargetIncVat.classList.add('hidden');
      if (discountedPriceTargetExcVat) discountedPriceTargetExcVat.classList.add('hidden');
      if (totalContainer) {
        totalContainer.classList.remove('bulk-order--total-discounted');
        totalContainer.classList.add('bulk-order--total-standard');
      }
    }
  }
}

customElements.define('bulk-order-list', BulkOrderList);
